// ------------------------------------------------------------------------------
// name: SingleUploader
// author: mudas
// created: 2024-10-15
// ------------------------------------------------------------------------------

import { isCanceledError } from 'common/utils';

import { RcFile } from 'antd/es/upload/interface';
import { FileACL } from 'common/types';
import { ChunkOption, ChunkStatus, SingleUploadUrl } from './types';
import { BaseUploader } from './BaseUploader';
import UploadError from './UploadError';

import service from 'common/service';
import { FastApiStatusMessage } from 'common/service/constants/fastApi';

export class SingleUploader extends BaseUploader {
  protected file?: RcFile;
  protected options?: ChunkOption;

  // 初始化上传的索引ID（获取预签名 url 的前置条件）
  protected uploadUrl: SingleUploadUrl | null = null;

  // 用于取消请求
  protected controller: AbortController | null = new AbortController();

  // --------------------------------------------------------------------------
  //
  // Class methods
  //
  // --------------------------------------------------------------------------

  stop() {
    this.controller?.abort();
    this.controller = null;
    this._paused = true;
  }

  resume() {
    // 若非正常途径来恢复，则停止上次的上传进程，重新开始
    // 正常途径会通过 stop() 方法停止上传，并置为 null
    if (this.controller) this.stop();

    // 单个文件从0开始重新上传
    this.controller = new AbortController();
    this._paused = false;
    this.execUpload();
  }

  async execUpload(retryCount: number = 3) {
    const { onFinished, onFailed, onProgress } = this.options || {};

    try {
      await service.chunkUploads.uploadFileData(
        { url: this.uploadUrl?.preSignedUploadUrl!, blob: this.file! },
        {
          signal: this.controller?.signal,
          onUploadProgress: event => onProgress?.((event.progress ?? 0) * 100, this.file!),
        },
      );

      onFinished?.({ ...this.uploadUrl!, url: this.uploadUrl?.downloadUrl }, this.file!);
    } catch (error: any) {
      // 取消请求，不派发事件，静默处理
      if (isCanceledError(error)) return;

      if (retryCount > 0) {
        console.warn(`File upload failed. Retrying... (${3 - retryCount + 1}/3)`);
        // 根据重试次数设置阶梯式等待时间
        const retryAttempt = 3 - retryCount + 1; // 计算当前是第几次重试
        const delayTime = retryAttempt === 1 ? 1000 : retryAttempt === 2 ? 2000 : 5000; // 分别等待1秒、2秒、5秒
        return new Promise<ChunkStatus>(resolve => {
          setTimeout(() => {
            this.execUpload(retryCount - 1);
          }, delayTime);
        });
      }

      // 非用户取消请求，才抛出完成事件
      onFailed?.(new UploadError(error.message || '文件上传失败', this.file, ChunkStatus.Error));
    }
  }

  /**
   * 开始上传文件（分片模式）
   * @param file
   * @param options
   */
  async upload(file: RcFile, options?: ChunkOption) {
    this.file = file;
    this.options = options;

    // 初始化文件上传id
    const params = {
      fileName: file.name,
      fileSize: file.size.toString(),
      contentType: file.type,
      acl: FileACL.Private,
      ...options?.params, // 从外部根据业务逻辑自定义进行覆盖
    };
    const res = await service.chunkUploads.getPreSignedUploadUrl(params);

    if (!res || res.code !== 0) {
      options?.onFailed?.(
        new UploadError(
          !res ? '预签服务异常' : (FastApiStatusMessage[res.code!] ?? '上传预签名链接获取失败'),
          file,
          ChunkStatus.Error,
        ),
      );
      return false;
    } else {
      this.uploadUrl = res.data;
    }

    // 需要异步返回，又不能破坏 execChunkUpload 并行执行的特性
    this.execUpload();
  }
}
